import { useMemo } from 'react';
import { fmtMoney0, fmtMoney, toAUD, AGEING_COLOR } from './b2bLib';

function Card({ label, value, sub, accent, onClick }) {
  return (
    <div onClick={onClick}
      style={{
        background: '#FAFAF8', border: '1px solid #EFEDE7', borderRadius: 12, padding: '14px 18px',
        cursor: onClick ? 'pointer' : 'default',
      }}>
      <div style={{ fontSize: 10, fontWeight: 500, color: '#8B9094', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: 700, color: accent || '#0F1419', marginTop: 3, fontVariantNumeric: 'tabular-nums' }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: '#5C6166', marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

export default function Dashboard({ invoices, customers, fx, report, onOpenInvoice, onGoReceivables }) {
  // Headline numbers come from b2b_report() (server-side, AUD-normalised).
  const r = report || {};
  const ageing = r.ageing || { current: 0, d1_30: 0, d31_60: 0, d60p: 0 };
  const ageBuckets = [
    { key: 'Current', label: 'Current', v: Number(ageing.current || 0) },
    { key: '1-30', label: '1–30 days', v: Number(ageing.d1_30 || 0) },
    { key: '31-60', label: '31–60 days', v: Number(ageing.d31_60 || 0) },
    { key: '60+', label: '60+ days', v: Number(ageing.d60p || 0) },
  ];
  const ageTotal = ageBuckets.reduce((s, b) => s + b.v, 0) || 1;

  // Per-currency outstanding (native) breakdown — Mo's "AUD headline + breakdown".
  const byCurrency = useMemo(() => {
    const m = {};
    invoices.filter(i => ['Issued', 'Part-Paid'].includes(i.status)).forEach(i => {
      m[i.currency] = (m[i.currency] || 0) + Number(i.balance || 0);
    });
    return Object.entries(m).sort((a, b) => toAUD(b[1], b[0], fx) - toAUD(a[1], a[0], fx));
  }, [invoices, fx]);

  const overdueList = (r.overdue_invoices || []).slice(0, 8);

  return (
    <div>
      {/* Receivables — money in. (Outbound Payments / net cash position can sit beside this later.) */}
      <div style={{ fontSize: 12, fontWeight: 700, color: '#5C6166', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 10 }}>
        Receivables — money in
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 12, marginBottom: 22 }}>
        <Card label="Total outstanding" value={fmtMoney0(r.total_outstanding_aud)} sub="AUD, all open invoices" />
        <Card label="Overdue" value={fmtMoney0(r.overdue_aud)} sub={`${r.overdue_count || 0} invoice${(r.overdue_count || 0) === 1 ? '' : 's'}`} accent={(r.overdue_aud > 0) ? '#dc2626' : '#0F1419'} onClick={onGoReceivables} />
        <Card label="Due this week" value={fmtMoney0(r.due_this_week_aud)} sub="Next 7 days" />
        <Card label="Collected this month" value={fmtMoney0(r.collected_this_month_aud)} sub="Payments received" accent="#0F8E6F" />
        <Card label="Active accounts" value={r.active_accounts || customers.length} sub="B2B customers" />
      </div>

      {/* Ageing bar */}
      <div style={{ background: '#fff', border: '1px solid #e4e4e7', borderRadius: 12, padding: '16px 18px', marginBottom: 22 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 12 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: '#0a0a0a' }}>Ageing</div>
          <div style={{ fontSize: 11, color: '#a1a1aa' }}>Outstanding by age, AUD</div>
        </div>
        <div style={{ display: 'flex', height: 14, borderRadius: 7, overflow: 'hidden', marginBottom: 12, background: '#f4f4f5' }}>
          {ageBuckets.map(b => b.v > 0 && (
            <div key={b.key} title={`${b.label}: ${fmtMoney0(b.v)}`}
              style={{ width: `${(b.v / ageTotal) * 100}%`, background: AGEING_COLOR[b.key] }} />
          ))}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12 }}>
          {ageBuckets.map(b => (
            <div key={b.key}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ width: 8, height: 8, borderRadius: 2, background: AGEING_COLOR[b.key] }} />
                <span style={{ fontSize: 11, color: '#5C6166' }}>{b.label}</span>
              </div>
              <div style={{ fontSize: 16, fontWeight: 700, color: '#0F1419', marginTop: 2, fontVariantNumeric: 'tabular-nums' }}>{fmtMoney0(b.v)}</div>
            </div>
          ))}
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        {/* Per-currency breakdown */}
        <div style={{ background: '#fff', border: '1px solid #e4e4e7', borderRadius: 12, padding: '16px 18px' }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: '#0a0a0a', marginBottom: 10 }}>Outstanding by currency</div>
          {byCurrency.length === 0 ? (
            <div style={{ fontSize: 12, color: '#a1a1aa' }}>Nothing outstanding.</div>
          ) : byCurrency.map(([cur, bal]) => (
            <div key={cur} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid #fafafa', fontSize: 13 }}>
              <span style={{ color: '#52525b' }}>{cur}</span>
              <span style={{ fontVariantNumeric: 'tabular-nums' }}>
                <strong>{fmtMoney(bal, cur)}</strong>
                {cur !== 'AUD' && <span style={{ color: '#a1a1aa', marginLeft: 8 }}>≈ {fmtMoney0(toAUD(bal, cur, fx))}</span>}
              </span>
            </div>
          ))}
        </div>

        {/* Overdue shortlist */}
        <div style={{ background: '#fff', border: '1px solid #e4e4e7', borderRadius: 12, padding: '16px 18px' }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: '#0a0a0a', marginBottom: 10 }}>Overdue — chase these</div>
          {overdueList.length === 0 ? (
            <div style={{ fontSize: 12, color: '#16a34a' }}>Nothing overdue. 🎉</div>
          ) : overdueList.map((o, i) => (
            <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid #fafafa', fontSize: 12 }}>
              <span style={{ color: '#0a0a0a' }}>{o.customer} <span style={{ color: '#a1a1aa' }}>· {o.invoice_ref || o.po_ref || '—'}</span></span>
              <span style={{ fontVariantNumeric: 'tabular-nums' }}>
                {fmtMoney(o.balance, o.currency)} <span style={{ color: '#dc2626', marginLeft: 6 }}>{o.days_overdue}d</span>
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
